import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { DollarSign, TrendingUp, Users, Download, Loader2, Building2, ShoppingBag } from "lucide-react";

const PaymentsTab = () => {
  const [splits, setSplits] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [clubEarnings, setClubEarnings] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('by-purchase'); // 'by-purchase', 'by-club', or 'pending-payouts'
  const [markingPaid, setMarkingPaid] = useState({});
  const [paymentModal, setPaymentModal] = useState({ show: false, club: null });

  useEffect(() => {
    fetchPaymentData();
  }, []);

  const fetchPaymentData = async () => {
    setLoading(true);
    try {
      const [splitsRes, revenueRes, earningsRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/admin/payments/splits`),
        axios.get(`${API}/admin/payments/platform-revenue`),
        axios.get(`${API}/admin/payments/club-earnings`),
        axios.get(`${API}/admin/payments/pending-payouts`)
      ]);
      setSplits(splitsRes.data);
      setRevenue(revenueRes.data);
      setClubEarnings(earningsRes.data);
      setPendingPayouts(payoutsRes.data);
    } catch (error) {
      console.error("Failed to fetch payment data:", error);
      toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  };
  
  const handleMarkAsPaid = async (clubId, transactionRef, paymentDate) => {
    setMarkingPaid({ ...markingPaid, [clubId]: true });
    try {
      await axios.post(`${API}/admin/payments/mark-paid`, {
        club_id: clubId,
        transaction_reference: transactionRef,
        payment_date: paymentDate
      });
      toast.success("Payment marked as paid!");
      fetchPaymentData();
      setPaymentModal({ show: false, club: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark payment as paid");
    } finally {
      setMarkingPaid({ ...markingPaid, [clubId]: false });
    }
  };
  
  const exportPendingPayoutsCSV = () => {
    // Revolut-friendly format
    const headers = ['Recipient Name', 'Account Number', 'IFSC/Swift Code', 'Bank Name', 'Amount', 'Currency', 'Country', 'Reference'];
    const rows = pendingPayouts.map(payout => [
      payout.club_name,
      payout.bank_account_number || 'N/A',
      payout.bank_ifsc_code || payout.bank_swift_code || 'N/A',
      payout.bank_name || 'N/A',
      payout.pending_amount.toFixed(2),
      payout.currency || 'EUR',
      payout.country || '',
      `Payout-${payout.club_id}-${new Date().toISOString().split('T')[0]}`
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pending-payouts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success("Pending payouts exported!");
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { EUR: "€", USD: "$", GBP: "£", INR: "₹" };
    return symbols[currency] || currency;
  };

  const exportToCSV = () => {
    if (view === 'by-purchase') {
      // Export purchases
      const headers = ['Purchase ID', 'Customer', 'Total Amount', 'Platform Fee (10%)', 'Clubs Share (90%)', 'Currency', 'Date'];
      const rows = splits.map(split => [
        split.purchase_id,
        split.customer_name,
        split.total_amount.toFixed(2),
        split.platform_fee.toFixed(2),
        split.distributable_amount.toFixed(2),
        split.currency,
        new Date(split.created_at).toLocaleDateString()
      ]);
      
      const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
      downloadCSV(csvContent, 'payment_splits_by_purchase.csv');
    } else {
      // Export by club
      const headers = ['Club Name', 'Total Earned', 'Total Paid Out', 'Pending Balance', 'Currency'];
      const rows = clubEarnings.map(club => [
        club.club_name,
        club.total_earned.toFixed(2),
        club.total_paid_out.toFixed(2),
        club.pending_balance.toFixed(2),
        club.currency
      ]);
      
      const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
      downloadCSV(csvContent, 'payment_splits_by_club.csv');
    }
    toast.success('CSV exported successfully');
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gold-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Payment Management</h2>
          <p className="text-gray-600">Track platform revenue and club earnings (90/10 split)</p>
        </div>
        <Button
          onClick={exportToCSV}
          className="!bg-green-600 hover:!bg-green-700 !text-white"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Stats Cards */}
      {revenue && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Sales</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {getCurrencySymbol(revenue.currency)}{revenue.total_sales.toFixed(2)}
                  </p>
                </div>
                <ShoppingBag className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Platform Revenue (10%)</p>
                  <p className="text-2xl font-bold text-gold-700">
                    {getCurrencySymbol(revenue.currency)}{revenue.total_revenue.toFixed(2)}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-gold-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Clubs Share (90%)</p>
                  <p className="text-2xl font-bold text-green-700">
                    {getCurrencySymbol(revenue.currency)}{revenue.total_distributed.toFixed(2)}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Transactions</p>
                  <p className="text-2xl font-bold text-purple-700">{revenue.transaction_count}</p>
                </div>
                <Users className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* View Toggle */}
      <div className="flex gap-2">
        <Button
          onClick={() => setView('by-purchase')}
          className={view === 'by-purchase' ? '!bg-gold-600 !text-white' : '!bg-gray-200 !text-gray-700'}
        >
          <ShoppingBag className="w-4 h-4 mr-2" />
          By Purchase
        </Button>
        <Button
          onClick={() => setView('by-club')}
          className={view === 'by-club' ? '!bg-gold-600 !text-white' : '!bg-gray-200 !text-gray-700'}
        >
          <Building2 className="w-4 h-4 mr-2" />
          By Club
        </Button>
        <Button
          onClick={() => setView('pending-payouts')}
          className={view === 'pending-payouts' ? '!bg-red-600 !text-white' : '!bg-gray-200 !text-gray-700'}
        >
          <DollarSign className="w-4 h-4 mr-2" />
          Pending Payouts (India)
          {pendingPayouts.length > 0 && (
            <Badge className="ml-2 !bg-red-500 !text-white">{pendingPayouts.length}</Badge>
          )}
        </Button>
      </div>

      {/* By Purchase View */}
      {view === 'by-purchase' && (
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4">Payment Splits by Purchase</h3>
          {splits.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No payment splits yet</p>
                <p className="text-sm text-gray-400 mt-2">Payment splits are created when customers purchase bundles</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {splits.map((split) => (
                <Card key={split.id} className="border-l-4 border-l-blue-500">
                  <CardHeader className="bg-blue-50 pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{split.customer_name}</CardTitle>
                        <CardDescription className="text-xs">
                          Purchase ID: {split.purchase_id}
                        </CardDescription>
                      </div>
                      <Badge className="bg-blue-600 text-white">
                        {getCurrencySymbol(split.currency)}{split.total_amount.toFixed(2)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="p-3 bg-gold-50 rounded">
                        <p className="text-xs text-gray-600">Platform Fee (10%)</p>
                        <p className="text-lg font-bold text-gold-700">
                          {getCurrencySymbol(split.currency)}{split.platform_fee.toFixed(2)}
                        </p>
                      </div>
                      <div className="p-3 bg-green-50 rounded">
                        <p className="text-xs text-gray-600">Clubs Share (90%)</p>
                        <p className="text-lg font-bold text-green-700">
                          {getCurrencySymbol(split.currency)}{split.distributable_amount.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    {/* Club Breakdown */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Club Breakdown:</h4>
                      <div className="space-y-2">
                        {split.club_splits.map((club, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-800">{club.club_name}</p>
                              <p className="text-xs text-gray-600">{club.hours} hours</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-green-700">
                                {getCurrencySymbol(split.currency)}{club.amount.toFixed(2)}
                              </p>
                              <Badge className={club.paid ? 'bg-green-600 text-white' : 'bg-yellow-600 text-white'}>
                                {club.paid ? 'Paid' : 'Pending'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <p className="text-xs text-gray-500 mt-4">
                      Date: {new Date(split.created_at).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* By Club View */}
      {view === 'by-club' && (
        <div>
          <h3 className="text-xl font-bold text-gray-800 mb-4">Earnings by Club</h3>
          {clubEarnings.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No club earnings yet</p>
                <p className="text-sm text-gray-400 mt-2">Earnings are tracked when clubs' hours are sold</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {clubEarnings.map((club) => (
                <Card key={club.club_id} className="border-l-4 border-l-green-500">
                  <CardHeader className="bg-green-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      {club.club_name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center p-2 bg-blue-50 rounded">
                        <p className="text-xs text-gray-600">Total Earned</p>
                        <p className="text-lg font-bold text-blue-700">
                          {getCurrencySymbol(club.currency)}{club.total_earned.toFixed(2)}
                        </p>
                      </div>
                      <div className="text-center p-2 bg-green-50 rounded">
                        <p className="text-xs text-gray-600">Paid Out</p>
                        <p className="text-lg font-bold text-green-700">
                          {getCurrencySymbol(club.currency)}{club.total_paid_out.toFixed(2)}
                        </p>
                      </div>
                      <div className="text-center p-2 bg-yellow-50 rounded">
                        <p className="text-xs text-gray-600">Pending</p>
                        <p className="text-lg font-bold text-yellow-700">
                          {getCurrencySymbol(club.currency)}{club.pending_balance.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    {club.stripe_verified && (
                      <div className="p-2 bg-green-100 border border-green-300 rounded text-center">
                        <p className="text-xs font-semibold text-green-800">✓ Stripe Verified</p>
                      </div>
                    )}

                    {club.last_payout_at && (
                      <p className="text-xs text-gray-500 text-center">
                        Last payout: {new Date(club.last_payout_at).toLocaleDateString()}
                      </p>
                    )}

                    <p className="text-xs text-gray-400 text-center">
                      Updated: {new Date(club.updated_at).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pending Payouts View */}
      {view === 'pending-payouts' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-800">Pending Payouts (India Only)</h3>
              <p className="text-sm text-gray-600">
                Indian clubs with unpaid earnings - ready for Revolut processing
                <span className="text-xs text-orange-600 ml-2">
                  • International clubs use Stripe Connect (automated)
                </span>
              </p>
            </div>
            {pendingPayouts.length > 0 && (
              <Button
                onClick={exportPendingPayoutsCSV}
                className="!bg-green-600 hover:!bg-green-700 !text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Export for Revolut
              </Button>
            )}
          </div>

          {pendingPayouts.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No pending payouts for Indian clubs</p>
                <p className="text-sm text-gray-400 mt-2">All Indian clubs have been paid up to date!</p>
                <p className="text-xs text-orange-600 mt-2">
                  International clubs are automatically paid via Stripe Connect
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Summary Card */}
              <Card className="border-2 border-red-300 bg-red-50">
                <CardContent className="p-6">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-gray-600">Clubs Awaiting Payment</p>
                      <p className="text-3xl font-bold text-red-700">{pendingPayouts.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Total Amount Due</p>
                      <p className="text-3xl font-bold text-red-700">
                        {getCurrencySymbol(pendingPayouts[0]?.currency || 'EUR')}
                        {pendingPayouts.reduce((sum, p) => sum + p.pending_amount, 0).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Countries</p>
                      <p className="text-3xl font-bold text-red-700">
                        {[...new Set(pendingPayouts.map(p => p.country))].length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payout Cards */}
              {pendingPayouts.map((payout) => (
                <Card key={payout.club_id} className="border-l-4 border-l-red-500">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-gray-800">{payout.club_name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline">{payout.country}</Badge>
                          {payout.club_type && (
                            <Badge className="!bg-blue-100 !text-blue-800">{payout.club_type}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-red-600">
                          {getCurrencySymbol(payout.currency)}{payout.pending_amount.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">Amount Due</p>
                      </div>
                    </div>

                    {/* Bank Details */}
                    <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded mb-4">
                      <div>
                        <p className="text-xs text-gray-600 font-semibold">Bank Name</p>
                        <p className="text-sm text-gray-800">{payout.bank_name || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 font-semibold">Account Holder</p>
                        <p className="text-sm text-gray-800">{payout.account_holder_name || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 font-semibold">Account Number</p>
                        <p className="text-sm text-gray-800 font-mono">{payout.bank_account_number || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600 font-semibold">
                          {payout.country === 'India' ? 'IFSC Code' : 'Swift/BIC'}
                        </p>
                        <p className="text-sm text-gray-800 font-mono">
                          {payout.bank_ifsc_code || payout.bank_swift_code || 'Not provided'}
                        </p>
                      </div>
                    </div>

                    {/* Earnings Breakdown */}
                    <div className="grid grid-cols-3 gap-2 mb-4">
                      <div className="p-2 bg-blue-50 rounded text-center">
                        <p className="text-xs text-gray-600">Total Earned</p>
                        <p className="text-sm font-bold text-blue-700">
                          {getCurrencySymbol(payout.currency)}{payout.total_earned.toFixed(2)}
                        </p>
                      </div>
                      <div className="p-2 bg-green-50 rounded text-center">
                        <p className="text-xs text-gray-600">Already Paid</p>
                        <p className="text-sm font-bold text-green-700">
                          {getCurrencySymbol(payout.currency)}{payout.total_paid_out.toFixed(2)}
                        </p>
                      </div>
                      <div className="p-2 bg-red-50 rounded text-center">
                        <p className="text-xs text-gray-600">Pending</p>
                        <p className="text-sm font-bold text-red-700">
                          {getCurrencySymbol(payout.currency)}{payout.pending_amount.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    {/* Action Button */}
                    <Button
                      onClick={() => setPaymentModal({ show: true, club: payout })}
                      className="w-full !bg-green-600 hover:!bg-green-700 !text-white"
                      disabled={markingPaid[payout.club_id]}
                    >
                      {markingPaid[payout.club_id] ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <DollarSign className="w-4 h-4 mr-2" />
                          Mark as Paid
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mark as Paid Modal */}
      {paymentModal.show && paymentModal.club && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Mark Payment as Paid</CardTitle>
              <CardDescription>
                Record payment details for {paymentModal.club.club_name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleMarkAsPaid(
                  paymentModal.club.club_id,
                  formData.get('transaction_ref'),
                  formData.get('payment_date')
                );
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Amount
                    </label>
                    <input
                      type="text"
                      disabled
                      value={`${getCurrencySymbol(paymentModal.club.currency)}${paymentModal.club.pending_amount.toFixed(2)}`}
                      className="w-full px-3 py-2 border rounded bg-gray-100 text-gray-700 font-bold"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Revolut Transaction Reference *
                    </label>
                    <input
                      type="text"
                      name="transaction_ref"
                      required
                      placeholder="e.g., REV-123456789"
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter the transaction ID from Revolut
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Date *
                    </label>
                    <input
                      type="date"
                      name="payment_date"
                      required
                      defaultValue={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      onClick={() => setPaymentModal({ show: false, club: null })}
                      className="flex-1 !bg-gray-300 hover:!bg-gray-400 !text-gray-800"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 !bg-green-600 hover:!bg-green-700 !text-white"
                    >
                      Confirm Payment
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PaymentsTab;
